import classNames from 'classnames'

import { ImageComponent } from '@apps-common/components/common'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_ACTIVATION_ALLOWED_PRODUCTS } from '@apps-common/config/product-codes'
import { UNIT_STATUS_CANCELLED, UNIT_STATUS_INACTIVE, UNIT_STATUS_PENDING_CANCELLATION } from '@apps-common/config/unit-filter'
import { IAppNavigation } from '@apps-common/hooks/useNavigation'
import { IPortalUnitsFilterProps } from '@apps-common/stores'
import { IManagementUnitSearchModel } from '@apps-common/types/management/unit'
import { IPortalUnitSearchModel } from '@apps-common/types/portal/unit'
import { ROUTE_ORDER_DETAILS, ROUTE_UNIT_ACTIVATION_DETAILS, ROUTE_UNIT_MANAGE } from '@apps-radionet-portal-root/src/config/private-routes'

import { ModalButton } from '@apps-common/components/modals'
import { toLocalTimestampStr } from '@apps-common/helpers/to-local-timestamp'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { PortalUnitDetailsModal } from '../../../modals/PortalUnitDetailsModal'
import { navigateActivationWithUnitDetails } from '../../../pages/activations/navigateActivationWithUnitDetails'

interface IActionProps {
  props: IManagementUnitSearchModel
  navigateForward: IAppNavigation['navigateForward']
}

const Actions: React.FC<IActionProps> = ({ props, navigateForward }) => (
  <div className='flex items-center justify-end gap-[5px]'>
    {props.orderId && (
      <ButtonRoundSm
        type='button'
        className='action-brand'
        callback={() => navigateForward({ routeUrl: ROUTE_ORDER_DETAILS.buildPath(props.orderId), route: ROUTE_ORDER_DETAILS })}
        tooltip={{ id: `device-order-${props.unitId}`, message: 'View related order' }}
      >
        <commonIcons.sbOrders />
      </ButtonRoundSm>
    )}
    {props.status === UNIT_STATUS_INACTIVE.value && UNIT_ACTIVATION_ALLOWED_PRODUCTS.includes(props.productId) && (
      <ButtonRoundSm
        type='button'
        className={props.unitActivationRequestID ? 'action-warning' : 'action-success'}
        callback={() => {
          if (props.unitActivationRequestID) {
            navigateForward({
              routeUrl: ROUTE_UNIT_ACTIVATION_DETAILS.buildPath(props.unitActivationRequestID),
              route: ROUTE_UNIT_ACTIVATION_DETAILS
            })
          } else {
            navigateActivationWithUnitDetails(navigateForward, props.unitId, props.serialNo, props.imei)
          }
        }}
        tooltip={{
          id: props.unitActivationRequestID
            ? `view-device-activation-request-${props.unitId}`
            : `create-device-activation-request-${props.unitId}`,
          message: props.unitActivationRequestID ? 'View activation' : 'Create activation'
        }}
      >
        <commonIcons.srActivations />
      </ButtonRoundSm>
    )}

    <ModalButton
      component={PortalUnitDetailsModal}
      classes=''
      componentProps={{
        status: props.status,
        unitId: props.unitId
      }}
      enterAnimation={modalSlideUpEnterAnimation}
      leaveAnimation={modalSlideUpLeaveAnimation}
      // confirmCallbackAfterDismiss={(data: string) => {}}
    >
      <ButtonRoundSm type='div' className='action-brand' tooltip={{ id: `device-details-${props.unitId}`, message: 'Details' }}>
        <commonIcons.details />
      </ButtonRoundSm>
    </ModalButton>
    <ButtonRoundSm
      type='button'
      className='bg-brand text-on-brand'
      callback={() => {
        navigateForward({ routeUrl: ROUTE_UNIT_MANAGE.buildPath(props.status, props.unitId), route: ROUTE_UNIT_MANAGE })
      }}
      tooltip={{ id: `manage-device-${props.unitId}`, message: 'Manage' }}
    >
      <commonIcons.arrowAngled />
    </ButtonRoundSm>
  </div>
)

interface IKeyProps {
  navigation: IAppNavigation
  isMonitoringCentre?: boolean
}

export const activeKeys = ({ navigation, isMonitoringCentre }: IKeyProps) => [
  {
    title: '',
    key: 'productImageUrl',
    static: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: any) => (
      <ImageComponent
        src={props.productImageUrl}
        alt='Product image'
        containerClasses='bg-border w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
        imageClasses='w-[28px] h-[28px]'
      />
    )
  },
  {
    title: 'Product',
    key: 'productName',
    changeable: true,
    thClasses: 'hidden xl:table-cell',
    tdClasses: 'hidden xl:table-cell'
  },
  {
    title: 'MCentre',
    key: 'monitoringCentre',
    changeable: true,
    optional: isMonitoringCentre,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Bureau',
    key: 'bureau',
    changeable: true,
    optional: !isMonitoringCentre,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'Technician',
    key: 'tech',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Account',
    key: 'accountNo',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Site',
    key: 'siteName',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },

  //
  // options - start

  {
    title: 'Network',
    key: 'networkType',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'IMEI',
    key: 'imei',
    optional: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'S/N',
    key: 'serialNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Mobile',
    key: 'mobileNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  // optional -end
  //
  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => <Actions props={props} navigateForward={navigation.navigateForward} />
  }
]

const inactiveKeys = ({ navigation, isMonitoringCentre }: IKeyProps) => [
  {
    title: '',
    key: 'productImageUrl',
    static: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: any) => (
      <ImageComponent
        src={props.productImageUrl}
        alt='Product image'
        containerClasses='bg-border w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
        imageClasses='w-[28px] h-[28px]'
      />
    )
  },
  {
    title: 'Product',
    key: 'productName',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'IMEI',
    key: 'imei',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'S/N',
    key: 'serialNo',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'Site',
    key: 'siteName',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Loaded on',
    key: 'dateLoaded',
    changeable: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IPortalUnitSearchModel) => <span>{toLocalTimestampStr(props.dateLoaded, 'DD/MM/YYYY')}</span>
  },

  // optional - start
  {
    title: 'Account',
    key: 'accountNo',
    optional: true,
    thClasses: '',
    tdClasses: '',
    Component: () => <span>TBC</span>
  },
  {
    title: 'Mobile',
    key: 'mobileNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Network',
    key: 'networkType',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },

  // optional - end

  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => <Actions props={props} navigateForward={navigation.navigateForward} />
  }
]

export const pendingCancellationKeys = ({ navigation, isMonitoringCentre }: IKeyProps) => [
  {
    title: '',
    key: 'productImageUrl',
    static: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: any) => (
      <ImageComponent
        src={props.productImageUrl}
        alt='Product image'
        containerClasses='bg-border w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
        imageClasses='w-[28px] h-[28px]'
      />
    )
  },
  {
    title: 'MCentre',
    key: 'monitoringCentre',
    changeable: true,
    hidden: isMonitoringCentre,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Bureau',
    key: 'bureau',
    changeable: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'Technician',
    key: 'tech',
    changeable: true,
    optional: !isMonitoringCentre,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Site',
    key: 'siteName',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Account',
    key: 'accountNo',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Cancels on',
    key: 'pendingCancellationDate',
    changeable: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IPortalUnitSearchModel) => <span>{toLocalTimestampStr(props.pendingCancellationDate || '', 'DD/MM/YYYY')}</span>
  },
  //
  // options - start
  {
    title: 'Product',
    key: 'productName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Network',
    key: 'networkType',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'IMEI',
    key: 'imei',
    optional: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'S/N',
    key: 'serialNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Mobile',
    key: 'mobileNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  // optional -end
  //
  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => <Actions props={props} navigateForward={navigation.navigateForward} />
  }
]

const cancelledKeys = ({ navigation, isMonitoringCentre }: IKeyProps) => [
  {
    title: '',
    key: 'productImageUrl',
    static: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: any) => (
      <ImageComponent
        src={props.productImageUrl}
        alt='Product image'
        containerClasses='bg-border w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
        imageClasses='w-[28px] h-[28px]'
      />
    )
  },
  {
    title: 'MCentre',
    key: 'monitoringCentre',
    changeable: true,
    hidden: isMonitoringCentre,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Bureau',
    key: 'bureau',
    changeable: true,
    thClasses: classNames('', !isMonitoringCentre && 'hidden lg:table-cell'),
    tdClasses: classNames('', !isMonitoringCentre && 'hidden lg:table-cell')
  },
  {
    title: 'Technician',
    key: 'tech',
    changeable: true,
    optional: !isMonitoringCentre,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'Site',
    key: 'siteName',
    changeable: true,
    thClasses: 'hidden xl:table-cell',
    tdClasses: 'hidden xl:table-cell'
  },
  {
    title: 'Account',
    key: 'accountNo',
    changeable: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Status',
    key: 'cancellationProcessed',
    changeable: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IPortalUnitSearchModel) => (
      <span>
        {props.cancellationProcessed ? (
          <span className='text-[0.8rem] bg-error text-on-error px-3 py-1 rounded-full'>Processed</span>
        ) : (
          <span className='text-[0.8rem] bg-warning text-on-warning px-3 py-1 rounded-full'>Pending</span>
        )}
      </span>
    )
  },
  {
    title: 'Cancel date',
    key: 'cancellationDate',
    changeable: true,
    thClasses: '',
    tdClasses: '',
    Component: (props: IPortalUnitSearchModel) => <span>{toLocalTimestampStr(props.cancellationDate || '', 'DD/MM/YYYY')}</span>
  },
  //
  // options - start
  {
    title: 'Product',
    key: 'productName',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Network',
    key: 'networkType',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'IMEI',
    key: 'imei',
    optional: true,
    thClasses: 'hidden lg:table-cell',
    tdClasses: 'hidden lg:table-cell'
  },
  {
    title: 'S/N',
    key: 'serialNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  {
    title: 'Mobile',
    key: 'mobileNo',
    optional: true,
    thClasses: '',
    tdClasses: ''
  },
  // optional -end
  //
  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right',
    tdClasses: '',
    Component: (props: IManagementUnitSearchModel) => <Actions props={props} navigateForward={navigation.navigateForward} />
  }
]

export const buildTableKeys = (navigation: IAppNavigation, filter: IPortalUnitsFilterProps, isMonitoringCentre?: boolean) => {
  if (filter.status === UNIT_STATUS_INACTIVE.value) {
    return inactiveKeys({ navigation, isMonitoringCentre })
  }

  if (filter.status === UNIT_STATUS_PENDING_CANCELLATION.value) {
    return pendingCancellationKeys({ navigation, isMonitoringCentre })
  }

  if (filter.status === UNIT_STATUS_CANCELLED.value) {
    return cancelledKeys({ navigation, isMonitoringCentre })
  }

  return activeKeys({ navigation, isMonitoringCentre })
}
